// src/components/StoreAdmin/ProductForm.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Stack,
  Typography,
  IconButton,
  Divider,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { createItem, updateItem } from '../../services/items';
import type { ItemDTO } from '../../dto/item/ItemDTO';
import type { CreateItemDTO } from '../../dto/item/CreateItemDTO';
import type { ItemOptionDTO } from '../../dto/item/ItemOptionDTO';
import { PackageDimension } from '../../model/PackageDimension';
import { Color, Size } from '../../model/enums';
import { showError, showSuccess } from '../Alert';
import { LIMITS } from '../../config/appConfig';
import '../../styles/ProductForm.css';

interface ProductFormProps {
  product: ItemDTO | null;
  onSuccess: () => void;
}

// Editable so we can allow "" before user chooses enum options
type EditableItemOption = Omit<ItemOptionDTO, 'color' | 'size'> & {
  color: Color | '';
  size: Size | '';
};

const VALID_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];
const isValidImage = (f: File) => VALID_IMAGE_TYPES.includes(f.type);

const MAX_FILE_MB = 8;
const MAX_TOTAL_MB = 24;
const bytesToMB = (b: number) => b / (1024 * 1024);

// tiny helper to compare arrays (used to detect “no change”)
const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

// Normalize option (prevents controlled/uncontrolled warnings)
const normalizeOption = (o: Partial<ItemOptionDTO>): EditableItemOption => ({
  id: o.id ?? '',
  color: (o.color as Color) ?? '',
  size: (o.size as Size) ?? '',
  price: typeof o.price === 'number' ? o.price : 0,
  sale: typeof o.sale === 'number' ? o.sale : 0,
  count: Number.isInteger(o.count as number) ? (o.count as number) : 1,
  isAvailable: typeof o.isAvailable === 'boolean' ? o.isAvailable : true,
});

// filename from URL (for titles/tooltips)
function filenameFromUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const last = u.pathname.split('/').filter(Boolean).pop() || 'file';
    return decodeURIComponent(last);
  } catch {
    const last = (url.split('?')[0] || '').split('/').pop() || 'file';
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  }
}

// object URL helpers
function useObjectUrl(file: File | null) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}
function useObjectUrls(files: File[]) {
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(
    () => () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    },
    [urls]
  );
  return urls;
}
const dedupeFiles = (files: File[]) => {
  const seen = new Set<string>();
  const out: File[] = [];
  for (const f of files) {
    const key = `${f.name}:${f.size}:${f.lastModified}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(f);
    }
  }
  return out;
};

export default function ProductForm({ product, onSuccess }: ProductFormProps) {
  // Core fields
  const [name, setName] = useState<string>(product?.name ?? '');
  const [description, setDescription] = useState<string>(
    product?.description ?? ''
  );
  const [price, setPrice] = useState<string>(
    product ? String(product.price) : ''
  );
  const [sale, setSale] = useState<string>(product ? String(product.sale) : '');
  const [currency, setCurrency] = useState<'CAD' | 'USD'>(
    (product?.currency as any) ?? 'CAD'
  );
  const [tags, setTags] = useState<string>(product?.tags?.join(', ') ?? '');
  const [isAvailable, setIsAvailable] = useState<boolean>(
    !!product?.isAvailable
  );

  // Dimensions
  const dims = product?.dimensions;
  const [width, setWidth] = useState<string>(dims ? String(dims.width) : '');
  const [length, setLength] = useState<string>(dims ? String(dims.length) : '');
  const [height, setHeight] = useState<string>(dims ? String(dims.height) : '');
  const [weight, setWeight] = useState<string>(dims ? String(dims.weight) : '');
  const [unit, setUnit] = useState<PackageDimension['unit']>(
    dims?.unit ?? 'IMPERIAL'
  );

  // Options
  const [itemOptions, setItemOptions] = useState<EditableItemOption[]>(
    (product?.itemOptions ?? []).map(normalizeOption)
  );

  // Existing media (edit mode)
  const [existingThumbUrl, setExistingThumbUrl] = useState<string | null>(
    product?.thumbnail ?? null
  );
  const [existingImages, setExistingImages] = useState<string[]>(
    product?.images ?? []
  );

  // Newly selected media
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [imagesFiles, setImagesFiles] = useState<File[]>([]);

  const thumbInputRef = useRef<HTMLInputElement | null>(null);
  const imgsInputRef = useRef<HTMLInputElement | null>(null);

  const thumbnailPreviewUrl = useObjectUrl(thumbnailFile);
  const imagePreviewUrls = useObjectUrls(imagesFiles);

  const [submitting, setSubmitting] = useState(false);

  // Validation
  const [formError, setFormError] = useState<string | null>(null);
  const [optionErrors, setOptionErrors] = useState<
    { color?: string; size?: string; price?: string; count?: string }[]
  >([]);

  const COLOR_OPTIONS = Object.values(Color);
  const SIZE_OPTIONS = Object.values(Size);

  // sync when product changes
  useEffect(() => {
    if (product) {
      setName(product.name ?? '');
      setDescription(product.description ?? '');
      setPrice(product.price != null ? String(product.price) : '');
      setSale(product.sale != null ? String(product.sale) : '');
      setCurrency((product.currency as any) ?? 'CAD');
      setTags(product.tags?.join(', ') ?? '');
      setIsAvailable(Boolean(product.isAvailable));

      setWidth(
        product.dimensions?.width != null
          ? String(product.dimensions.width)
          : ''
      );
      setLength(
        product.dimensions?.length != null
          ? String(product.dimensions.length)
          : ''
      );
      setHeight(
        product.dimensions?.height != null
          ? String(product.dimensions.height)
          : ''
      );
      setWeight(
        product.dimensions?.weight != null
          ? String(product.dimensions.weight)
          : ''
      );
      setUnit(product.dimensions?.unit ?? 'IMPERIAL');

      setItemOptions((product.itemOptions ?? []).map(normalizeOption));

      // media
      setExistingThumbUrl(product.thumbnail ?? null);
      setExistingImages([...(product.images ?? [])]);
    } else {
      setName('');
      setDescription('');
      setPrice('');
      setSale('');
      setCurrency('CAD');
      setTags('');
      setIsAvailable(false);
      setWidth('');
      setLength('');
      setHeight('');
      setWeight('');
      setUnit('IMPERIAL');
      setItemOptions([]);
      setExistingThumbUrl(null);
      setExistingImages([]);
    }
    // clear local picks
    setThumbnailFile(null);
    setImagesFiles([]);
    setFormError(null);
    setOptionErrors([]);
    if (thumbInputRef.current) thumbInputRef.current.value = '';
    if (imgsInputRef.current) imgsInputRef.current.value = '';
  }, [product]);

  // option helpers
  const handleOptionChange = (
    idx: number,
    field: keyof EditableItemOption,
    value: any
  ) => {
    setItemOptions((opts) =>
      opts.map((o, i) => (i === idx ? { ...o, [field]: value } : o))
    );
  };
  const addOption = () => {
    setItemOptions((opts) => [
      ...opts,
      {
        id: '',
        color: '',
        size: '',
        price: 0,
        sale: 0,
        count: 1,
        isAvailable: true,
      },
    ]);
  };
  const removeOption = (idx: number) => {
    setItemOptions((opts) => opts.filter((_, i) => i !== idx));
    setOptionErrors((errs) => errs.filter((_, i) => i !== idx));
  };

  // utils
  const toNum = (v: string) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
  };
  const parseTags = (raw: string) =>
    raw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

  const validate = (): boolean => {
    setFormError(null);
    const errors: {
      color?: string;
      size?: string;
      price?: string;
      count?: string;
    }[] = [];

    if (!name.trim()) {
      const msg = 'Name is required.';
      setFormError(msg);
      showError(msg);
      return false;
    }
    if (!description.trim()) {
      const msg = 'Description is required.';
      setFormError(msg);
      showError(msg);
      return false;
    }
    const priceNum = toNum(price);
    if (isNaN(priceNum) || priceNum < LIMITS.ITEM_MIN_PRICE) {
      const msg = `Price must be at least ${LIMITS.ITEM_MIN_PRICE}.`;
      setFormError(msg);
      showError(msg);
      return false;
    }
    if (!currency) {
      const msg = 'Currency is required.';
      setFormError(msg);
      showError(msg);
      return false;
    }

    const computedTags = parseTags(tags);
    if (computedTags.length > LIMITS.MAX_TAGS_COUNT) {
      const msg = `You can add up to ${LIMITS.MAX_TAGS_COUNT} tags.`;
      setFormError(msg);
      showError(msg);
      return false;
    }
    const badTag = computedTags.find(
      (t) => t.length < LIMITS.MIN_TAG_LEN || t.length > LIMITS.MAX_TAG_LEN
    );
    if (badTag) {
      const msg = `Each tag must be ${LIMITS.MIN_TAG_LEN}–${LIMITS.MAX_TAG_LEN} chars. Offender: "${badTag}".`;
      setFormError(msg);
      showError(msg);
      return false;
    }

    const w = toNum(width),
      l = toNum(length),
      h = toNum(height),
      wt = toNum(weight);
    if ([w, l, h, wt].some((v) => !Number.isFinite(v))) {
      const msg = 'Delivery box dimensions must be valid numbers.';
      setFormError(msg);
      showError(msg);
      return false;
    }
    if (
      w < LIMITS.MIN_PACKAGE_WIDTH_IN_INCH ||
      w > LIMITS.MAX_PACKAGE_WIDTH_IN_INCH
    ) {
      showError(
        `Width must be between ${LIMITS.MIN_PACKAGE_WIDTH_IN_INCH} and ${LIMITS.MAX_PACKAGE_WIDTH_IN_INCH} in.`
      );
      return false;
    }
    if (
      l < LIMITS.MIN_PACKAGE_LENGTH_IN_INCH ||
      l > LIMITS.MAX_PACKAGE_LENGTH_IN_INCH
    ) {
      showError(
        `Length must be between ${LIMITS.MIN_PACKAGE_LENGTH_IN_INCH} and ${LIMITS.MAX_PACKAGE_LENGTH_IN_INCH} in.`
      );
      return false;
    }
    if (
      h < LIMITS.MIN_PACKAGE_HEIGHT_IN_INCH ||
      h > LIMITS.MAX_PACKAGE_HEIGHT_IN_INCH
    ) {
      showError(
        `Height must be between ${LIMITS.MIN_PACKAGE_HEIGHT_IN_INCH} and ${LIMITS.MAX_PACKAGE_HEIGHT_IN_INCH} in.`
      );
      return false;
    }
    if (
      wt < LIMITS.MIN_PACKAGE_WEIGHT_IN_LB ||
      wt > LIMITS.MAX_PACKAGE_WEIGHT_IN_LB
    ) {
      showError(
        `Weight must be between ${LIMITS.MIN_PACKAGE_WEIGHT_IN_LB} and ${LIMITS.MAX_PACKAGE_WEIGHT_IN_LB} lb.`
      );
      return false;
    }

    if (itemOptions.length === 0) {
      const msg = 'Add at least one item option.';
      setFormError(msg);
      showError(msg);
      return false;
    }
    let anyErr = false;
    itemOptions.forEach((opt, idx) => {
      const e: {
        color?: string;
        size?: string;
        price?: string;
        count?: string;
      } = {};
      if (!opt.color) {
        e.color = 'Select a color';
        anyErr = true;
      }
      if (!opt.size) {
        e.size = 'Select a size';
        anyErr = true;
      }
      if (!Number.isFinite(opt.price) || opt.price < 0) {
        e.price = 'Price must be ≥ 0';
        anyErr = true;
      }
      if (!Number.isInteger(opt.count) || opt.count < 0) {
        e.count = 'Count must be ≥ 0';
        anyErr = true;
      }
      errors[idx] = e;
    });
    setOptionErrors(errors);
    if (anyErr) {
      showError('Please complete all required fields for item options.');
      return false;
    }

    if (!product && !thumbnailFile) {
      const msg = 'Thumbnail is required for a new product.';
      setFormError(msg);
      showError(msg);
      return false;
    }

    const perFileTooBig =
      (thumbnailFile && bytesToMB(thumbnailFile.size) > MAX_FILE_MB) ||
      imagesFiles.some((f) => bytesToMB(f.size) > MAX_FILE_MB);
    if (perFileTooBig) {
      showError(`Each image must be ≤ ${MAX_FILE_MB} MB.`);
      return false;
    }
    const totalBytes =
      (thumbnailFile?.size ?? 0) + imagesFiles.reduce((s, f) => s + f.size, 0);
    if (bytesToMB(totalBytes) > MAX_TOTAL_MB) {
      showError(`Total upload must be ≤ ${MAX_TOTAL_MB} MB.`);
      return false;
    }

    return true;
  };

  // Clear handlers (with “at least one image” guard)
  const handleClearThumbnail = () => {
    setThumbnailFile(null);
    setExistingThumbUrl(null);
    if (thumbInputRef.current) thumbInputRef.current.value = '';
  };
  const handleRemoveNewImageAt = (idx: number) => {
    const remaining = existingImages.length + (imagesFiles.length - 1);
    if (remaining < 1) {
      showError('At least one image is required.');
      return;
    }
    setImagesFiles((prev) => prev.filter((_, i) => i !== idx));
    if (imgsInputRef.current) imgsInputRef.current.value = '';
  };
  const handleRemoveExistingImageAt = (idx: number) => {
    const remaining = existingImages.length - 1 + imagesFiles.length;
    if (remaining < 1) {
      showError('At least one image is required.');
      return;
    }
    setExistingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);

    const dimensions: PackageDimension = {
      width: toNum(width),
      length: toNum(length),
      height: toNum(height),
      weight: toNum(weight),
      unit,
    };

    const finalizedOptions: ItemOptionDTO[] = itemOptions.map((o) => ({
      ...o,
      color: o.color as Color,
      size: o.size as Size,
    }));

    const computedTags = parseTags(tags);

    const createPayload: CreateItemDTO = {
      name: name.trim(),
      description: description.trim(),
      price: toNum(price),
      sale: sale ? toNum(sale) : 0,
      currency,
      tags: computedTags,
      isAvailable,
      itemOptions: finalizedOptions,
      dimensions,
    };

    try {
      if (product && product.id) {
        // compute whether images changed
        const originalImages = product.images ?? [];
        const imagesChanged =
          imagesFiles.length > 0 ||
          !arraysEqual(existingImages, originalImages);

        // if user made changes and ended up with zero images and no new files -> block
        if (
          imagesChanged &&
          existingImages.length === 0 &&
          imagesFiles.length === 0
        ) {
          showError('At least one image is required.');
          setSubmitting(false);
          return;
        }

        // Build payload: only include images keep-list when changed; otherwise send null
        const updateBase: ItemDTO = {
          ...product,
          name: createPayload.name,
          description: createPayload.description,
          price: createPayload.price,
          sale: createPayload.sale,
          currency: createPayload.currency,
          tags: createPayload.tags,
          isAvailable: createPayload.isAvailable,
          itemOptions: createPayload.itemOptions,
          dimensions,
          thumbnail: (existingThumbUrl as any) ?? null,
          images: existingImages, // temporary; we’ll override below if unchanged
          store: product.store,
          createdAt: product.createdAt,
          updatedAt: new Date().toISOString(),
        };

        // prepare files to send
        const thumbToSend: File | null = thumbnailFile ?? null;
        const imagesToSend: File[] | null = imagesFiles.length
          ? imagesFiles
          : null;

        // finalize itemDto respecting “send null if no change”
        const itemPayload: any = {
          ...updateBase,
          images: imagesChanged ? existingImages : null,
        };

        await updateItem(
          itemPayload as ItemDTO,
          thumbToSend,
          imagesToSend,
          imagesChanged ? existingImages : null
        );
        showSuccess('Product updated.');
        onSuccess();
      } else {
        // CREATE
        await createItem(createPayload, thumbnailFile!, imagesFiles);
        showSuccess('Product created.');
        onSuccess();
      }
    } catch (err: any) {
      const msg =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.message ?? 'Failed to save product.';
      setFormError(msg);
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      className="product-form"
    >
      <Stack spacing={2}>
        {formError && (
          <Typography variant="body2" color="error" className="pf-error">
            {formError}
          </Typography>
        )}

        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          required
          multiline
          minRows={3}
        />

        <div className="pf-row">
          <TextField
            className="pf-field"
            label="Price"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
            inputProps={{ min: LIMITS.ITEM_MIN_PRICE, step: '0.01' }}
          />
          <TextField
            className="pf-field"
            label="Sale (%)"
            type="number"
            value={sale}
            onChange={(e) => setSale(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
          />
          <FormControl className="pf-field" required>
            <InputLabel>Currency</InputLabel>
            <Select
              value={currency}
              label="Currency"
              onChange={(e) => setCurrency(e.target.value as any)}
              required
            >
              <MenuItem value="CAD">CAD</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </Select>
          </FormControl>
        </div>

        <TextField
          label="Tags (comma separated)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          fullWidth
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(isAvailable)}
              onChange={(e) => setIsAvailable(e.target.checked)}
            />
          }
          label="Available"
        />

        <Divider />

        {/* Dimensions */}
        <div className="pf-section">
          <div className="pf-section-title">Delivery Box Dimensions</div>
          <div className="pf-dimensions">
            <TextField
              label="Box Width (in)"
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              required
              inputProps={{
                min: LIMITS.MIN_PACKAGE_WIDTH_IN_INCH,
                max: LIMITS.MAX_PACKAGE_WIDTH_IN_INCH,
                step: '0.01',
              }}
            />
            <TextField
              label="Box Length (in)"
              type="number"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              required
              inputProps={{
                min: LIMITS.MIN_PACKAGE_LENGTH_IN_INCH,
                max: LIMITS.MAX_PACKAGE_LENGTH_IN_INCH,
                step: '0.01',
              }}
            />
            <TextField
              label="Box Height (in)"
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              required
              inputProps={{
                min: LIMITS.MIN_PACKAGE_HEIGHT_IN_INCH,
                max: LIMITS.MAX_PACKAGE_HEIGHT_IN_INCH,
                step: '0.01',
              }}
            />
            <TextField
              label="Box Weight (lb)"
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              required
              inputProps={{
                min: LIMITS.MIN_PACKAGE_WEIGHT_IN_LB,
                max: LIMITS.MAX_PACKAGE_WEIGHT_IN_LB,
                step: '0.01',
              }}
            />
            <FormControl required>
              <InputLabel>Unit</InputLabel>
              <Select
                value={unit}
                label="Unit"
                onChange={(e) => setUnit(e.target.value as any)}
                required
              >
                <MenuItem value="IMPERIAL">IMPERIAL</MenuItem>
                <MenuItem value="METRIC">METRIC</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {/* Options */}
        <div className="pf-section">
          <div className="pf-section-title">Item Options</div>
          <div className="pf-options">
            {itemOptions.map((opt, idx) => {
              const err = optionErrors[idx] || {};
              return (
                <div key={idx} className="pf-option-row">
                  <FormControl required error={!!err.color}>
                    <InputLabel>Color</InputLabel>
                    <Select
                      value={opt.color}
                      label="Color"
                      onChange={(e) =>
                        handleOptionChange(
                          idx,
                          'color',
                          e.target.value as Color | ''
                        )
                      }
                      renderValue={(v) => (v ? String(v) : '')}
                    >
                      {COLOR_OPTIONS.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                    {err.color && <FormHelperText>{err.color}</FormHelperText>}
                  </FormControl>

                  <FormControl required error={!!err.size}>
                    <InputLabel>Size</InputLabel>
                    <Select
                      value={opt.size}
                      label="Size"
                      onChange={(e) =>
                        handleOptionChange(
                          idx,
                          'size',
                          e.target.value as Size | ''
                        )
                      }
                      renderValue={(v) => (v ? String(v) : '')}
                    >
                      {SIZE_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                    {err.size && <FormHelperText>{err.size}</FormHelperText>}
                  </FormControl>

                  <TextField
                    label="Price"
                    type="number"
                    value={String(opt.price)}
                    onChange={(e) =>
                      handleOptionChange(
                        idx,
                        'price',
                        parseFloat(e.target.value) || 0
                      )
                    }
                    required
                    error={!!err.price}
                    helperText={err.price}
                    inputProps={{ min: 0, step: '0.01' }}
                  />

                  <TextField
                    label="Sale (%)"
                    type="number"
                    value={String(opt.sale ?? 0)}
                    onChange={(e) =>
                      handleOptionChange(
                        idx,
                        'sale',
                        parseFloat(e.target.value) || 0
                      )
                    }
                    inputProps={{ min: 0, step: '0.01' }}
                  />

                  <TextField
                    label="Count"
                    type="number"
                    value={String(opt.count)}
                    onChange={(e) =>
                      handleOptionChange(
                        idx,
                        'count',
                        parseInt(e.target.value) || 0
                      )
                    }
                    required
                    error={!!err.count}
                    helperText={err.count}
                    inputProps={{ min: 0, step: '1' }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(opt.isAvailable)}
                        onChange={(e) =>
                          handleOptionChange(
                            idx,
                            'isAvailable',
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Available"
                  />

                  <IconButton
                    onClick={() => removeOption(idx)}
                    aria-label="remove option"
                  >
                    <RemoveIcon />
                  </IconButton>
                </div>
              );
            })}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addOption}
              size="small"
              className="pf-add-option"
            >
              Add Option
            </Button>
          </div>
        </div>

        {/* Files */}
        <div className="pf-section">
          {/* THUMBNAIL */}
          <div className="pf-files-grid">
            <div className="pf-file-label">Thumbnail</div>

            <div className="pf-image-list">
              {existingThumbUrl && !thumbnailFile && (
                <div className="pf-image-row pf-thumb-row">
                  <div
                    className="pf-img-wrap"
                    title={filenameFromUrl(existingThumbUrl)}
                  >
                    <img
                      src={existingThumbUrl}
                      alt="Current thumbnail"
                      className="pf-img"
                    />
                    <IconButton
                      size="small"
                      className="pf-img-clear"
                      aria-label="remove thumbnail"
                      onClick={handleClearThumbnail}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </div>
                </div>
              )}

              {thumbnailPreviewUrl && (
                <div className="pf-image-row pf-thumb-row">
                  <div className="pf-img-wrap" title={thumbnailFile?.name}>
                    <img
                      src={thumbnailPreviewUrl}
                      alt="New thumbnail"
                      className="pf-img"
                    />
                    <IconButton
                      size="small"
                      className="pf-img-clear"
                      aria-label="clear thumbnail"
                      onClick={handleClearThumbnail}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </div>
                </div>
              )}
            </div>

            <div className="pf-file-actions">
              <Button variant="outlined" component="label" size="small">
                {thumbnailFile ? 'Change File' : 'Upload File'}
                <input
                  ref={thumbInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  required={!product}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (!isValidImage(f)) {
                      showError(
                        'Invalid image type. Allowed: JPG, PNG, WEBP, GIF'
                      );
                      e.currentTarget.value = '';
                      return;
                    }
                    if (bytesToMB(f.size) > MAX_FILE_MB) {
                      showError(`Each image must be ≤ ${MAX_FILE_MB} MB.`);
                      e.currentTarget.value = '';
                      return;
                    }
                    const totalBytes =
                      f.size + imagesFiles.reduce((s, x) => s + x.size, 0);
                    if (bytesToMB(totalBytes) > MAX_TOTAL_MB) {
                      showError(`Total upload must be ≤ ${MAX_TOTAL_MB} MB.`);
                      e.currentTarget.value = '';
                      return;
                    }
                    setExistingThumbUrl(null);
                    setThumbnailFile(f);
                    if (thumbInputRef.current) thumbInputRef.current.value = '';
                  }}
                />
              </Button>
              {(existingThumbUrl || thumbnailFile) && (
                <Button size="small" onClick={handleClearThumbnail}>
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* IMAGES */}
          <div className="pf-files-grid" style={{ marginTop: 10 }}>
            <div className="pf-file-label">Images</div>

            <div className="pf-image-list">
              {existingImages.map((u, i) => (
                <div className="pf-image-row" key={`exist-${i}`}>
                  <div className="pf-img-wrap" title={filenameFromUrl(u)}>
                    <img
                      src={u}
                      alt={`Existing image ${i + 1}`}
                      className="pf-img"
                    />
                    <IconButton
                      size="small"
                      className="pf-img-clear"
                      aria-label={`remove existing image ${i + 1}`}
                      onClick={() => handleRemoveExistingImageAt(i)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </div>
                </div>
              ))}

              {imagesFiles.map((f, i) => (
                <div className="pf-image-row" key={`new-${i}`}>
                  <div className="pf-img-wrap" title={f.name}>
                    <img
                      src={imagePreviewUrls[i]}
                      alt={`New image ${i + 1}`}
                      className="pf-img"
                    />
                    <IconButton
                      size="small"
                      className="pf-img-clear"
                      aria-label={`remove ${f.name}`}
                      onClick={() => handleRemoveNewImageAt(i)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>

            <div className="pf-file-actions">
              <Button variant="outlined" component="label" size="small">
                {existingImages.length + imagesFiles.length > 0
                  ? 'Add More'
                  : 'Upload'}
                <input
                  ref={imgsInputRef}
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={(e) => {
                    const picked = Array.from(e.target.files ?? []);
                    if (!picked.length) return;
                    const badType = picked.find((x) => !isValidImage(x));
                    if (badType) {
                      showError(
                        'Some images are invalid. Allowed: JPG, PNG, WEBP, GIF'
                      );
                      e.currentTarget.value = '';
                      return;
                    }
                    const tooBig = picked.find(
                      (x) => bytesToMB(x.size) > MAX_FILE_MB
                    );
                    if (tooBig) {
                      showError(`Each image must be ≤ ${MAX_FILE_MB} MB.`);
                      e.currentTarget.value = '';
                      return;
                    }
                    const newBytes = picked.reduce((s, x) => s + x.size, 0);
                    const currentBytes =
                      (thumbnailFile?.size ?? 0) +
                      imagesFiles.reduce((s, x) => s + x.size, 0);
                    if (bytesToMB(currentBytes + newBytes) > MAX_TOTAL_MB) {
                      showError(`Total upload must be ≤ ${MAX_TOTAL_MB} MB.`);
                      e.currentTarget.value = '';
                      return;
                    }
                    setImagesFiles((prev) => dedupeFiles([...prev, ...picked]));
                    if (imgsInputRef.current) imgsInputRef.current.value = '';
                  }}
                />
              </Button>
              {(existingImages.length > 0 || imagesFiles.length > 0) && (
                <Button
                  size="small"
                  onClick={() => {
                    // guard: don’t allow clearing all if no new files will be added
                    if (imagesFiles.length === 0 && existingImages.length === 0)
                      return;
                    setExistingImages([]);
                    setImagesFiles([]);
                    if (imgsInputRef.current) imgsInputRef.current.value = '';
                  }}
                >
                  Clear All
                </Button>
              )}
            </div>
          </div>
        </div>

        <Button type="submit" variant="contained" disabled={submitting}>
          {submitting
            ? 'Saving...'
            : product
            ? 'Save Changes'
            : 'Create Product'}
        </Button>
      </Stack>
    </Box>
  );
}
